import React, { useContext, useRef } from "react";
import { Fragment } from "react";
import { Typography } from "../../libraries/material/core"
import { useScrollToTop } from "../../hooks"
import { Helmet } from "../../libraries/Helmet"
import { PageHeadingContext } from "../context";

/**
 * import { PageHeading, withPageHeading, PageHeadingContext } from "@osu/react-ui"
 * 
 * At the App level, wrap your component with withPageHeading for PageHeadingContext to be constructed. 
 * In your main component, render <PageHeading />, preferably as the first child of main.
 * 
 * The responsibility of this component is to promote accessibility upon single page apps. When the heading is changed, 
 * i.e. when the page changes, the focus will change to the updated heading. Page title will automatically update. The 
 * page title should be a combination of the application name, and page heading.
 * 
 * To update the application name (`App.js`): 
 * 
 * ```
 *   const { setApplicationName } = useContext(PageHeadingContext);
 *   useEffect(() => {
 *          setApplicationName("React Amplify App");
 *   }, [setApplicationName]);
 * 
 * ```
 * 
 *   To update the page heading (Route Renderable View): 
 * 
 * ```
 *   const { setHeading } = useContext(PageHeadingContext);
 *   useEffect(() => {
 *          setHeading(title);
 *    }, [setHeading,title]);
 * ``` 
**/

const PageHeading = (props = {}) => {
  const headerRef = useRef();
  const { applicationName: propsAppName, ...rest } = props
  const context = useContext(PageHeadingContext);
  const { heading = '', pageTitle: title = "", applicationName: appName = "", options = {} } = context || {}
  useScrollToTop({ ref: headerRef });

  const applicationName = propsAppName || appName
  let { ...typographyProps } = Object.assign({}, options, rest)
  
  let pageTitle = title
  if(!pageTitle) {
    pageTitle = heading 
  }
  if(applicationName && !pageTitle.includes(applicationName)) {
    pageTitle += ` - ${applicationName}`
  }
  if(typeof pageTitle !== "string") pageTitle = (typeof applicationName === "string" ? applicationName : "")

  return (
    <Fragment>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Typography
        ref={headerRef}
        {...typographyProps}
      >
        {heading}
      </Typography>
    </Fragment>
  );
};

PageHeading.defaultProps = {
  variant: "h1",
  component: "h1",
  className: "",
  tabIndex: '-1'
};

export default PageHeading;
